import React from 'react';
import { isEqual } from 'lodash';
import { getDateString, getTimezoneAbbr } from 'js/lib/DateUtils';
import { Log } from '../../types';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { ReactComponent as ExpandIcon } from './icons/expand-icon.svg';
import { LogCopyIconButton } from '../../components/LogCopyIconButton';
import styles from './LogLine.module.scss';

type BaseProps = {
  logObject: Log;
  lineNumber: number | string;
  lineNumberElementWidth: number;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const areEquals = (prevProps: BaseProps, nextProps: BaseProps): boolean => {
  const isLogObjectEqual = isEqual(prevProps.logObject, nextProps.logObject);
  const isLineNumberEqual = prevProps.lineNumber === nextProps.lineNumber;
  const isLineNumberElementWidthEqual =
    prevProps.lineNumberElementWidth === nextProps.lineNumberElementWidth;
  return isLogObjectEqual && isLineNumberEqual && isLineNumberElementWidthEqual;
};

export const logSeverityClassMap: Record<string, string> = {
  ERROR: styles.logSeverityError,
  WARNING: styles.logSeverityWarning,
  INFO: styles.logSeverityInfo,
};

const LogLineComponent = (props: BaseProps) => {
  const { logObject, lineNumber, lineNumberElementWidth, onClick, onKeyDown } = props;

  const enableFlowInsightsV2 = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');

  const { timestamp, resource_type, resource_id, severity, log_type, log } = logObject;

  const showLogType = log_type === 'EVENT';
  const timezone = getTimezoneAbbr();
  const timestampString = getDateString(timestamp, 'YYYY-MM-DD HH:mm:ss');

  const logSeverityClass = logSeverityClassMap[severity] || styles.logSeverityInfo;

  const safeTimeStampwithBrackets = timestamp ? `[${timestampString} ${timezone}]` : null;
  const safeResourceTypeWithId =
    resource_type && resource_id ? `[${resource_type} ${resource_id}]` : null;
  // Run ID logic for V2
  const runId = enableFlowInsightsV2 ? logObject?.run_id : undefined;
  const hasRunId = runId != null;
  const runIdDisplay = runId ? String(runId) : '-';
  const runIdClassName = hasRunId
    ? styles.logLineRunId
    : `${styles.logLineRunId} ${styles.logLineRunIdPlaceholder}`;

  let formattedLine = '';

  formattedLine += ' ';

  if (showLogType) {
    formattedLine += '[';
    formattedLine += log_type;
    formattedLine += '] ';
  }

  formattedLine += log ?? '';

  return (
    <>
      <div
        className={styles.logLine}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`View details for log line ${lineNumber}`}
      >
        <LogCopyIconButton
          log={logObject}
          className={styles.copyButton}
          copiedClassName={styles.copyButton_copied}
        />

        <div className={styles.logLineNumberContainer} style={{ width: lineNumberElementWidth }}>
          <ExpandIcon className={styles.logLineExpandIcon} aria-label="View log details" />
          <span className={styles.logLineNumber}>{lineNumber}</span>
        </div>

        <div className={styles.logLineTextContent}>
          {safeTimeStampwithBrackets && (
            <span className={styles.logLineTimestamp}>{safeTimeStampwithBrackets}</span>
          )}
          {safeTimeStampwithBrackets && ' '}
          {enableFlowInsightsV2 && (
            <>
              <span className={runIdClassName}>{runIdDisplay}</span>{' '}
            </>
          )}
          <span className={logSeverityClass}>{severity}</span>{' '}
          {safeResourceTypeWithId && (
            <>
              <span className={styles.logLineResourceTypeId}>{safeResourceTypeWithId}</span>{' '}
            </>
          )}
          <span className={styles.logLineContent}>{formattedLine.trim() || ''}</span>
        </div>
      </div>
    </>
  );
};

export const LogLine = React.memo(LogLineComponent, areEquals);
LogLine.displayName = 'LogLine';
